// src/layouts/Admin/Firebase.js
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { resolveApiUrl } from "../../api/apiBase";

const FCM_BROWSER_TOKEN_STORAGE_KEY = "bw_fcm_browser_token";
const FCM_INSTALLATION_ID_STORAGE_KEY = "bw_fcm_installation_id";
const FCM_PENDING_CLEANUP_STORAGE_KEY = "bw_fcm_pending_cleanup";
const FCM_SERVICE_WORKER_PATH = "/firebase-messaging-sw.js";
const FCM_VAPID_KEY =
  "BOBC0SR1xDcPqY41ekSVHrOePM-5gD9d_37NnRXHMH0QFb55HznNVbFRMskiw_Lo_ZBhZSfSE2S2bkR1KwdB2jg";

let registrationPromise = null;
let lastRegistrationSignature = null;
let foregroundListenerAttached = false;

const firebaseConfig = {
  apiKey: "AIzaSyCcsD4kGGTsN_TWFaA6G2t_y4pW1GTiPcY",
  authDomain: "blackwhitecms.firebaseapp.com",
  projectId: "blackwhitecms",
  storageBucket: "blackwhitecms.appspot.com",
  messagingSenderId: "46638659133",
  appId: "1:46638659133:web:9fddc93e4073f8bdf89c54",
};

const app = initializeApp(firebaseConfig);

const readStoredAuthToken = (override) => {
  if (override) {
    return override;
  }

  return typeof localStorage !== "undefined"
    ? localStorage.getItem("token")
    : null;
};

const readStoredMessagingToken = () => {
  return typeof localStorage !== "undefined"
    ? localStorage.getItem(FCM_BROWSER_TOKEN_STORAGE_KEY)
    : null;
};

const createInstallationId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `bw-fcm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getOrCreateInstallationId = () => {
  if (typeof localStorage === "undefined") {
    return createInstallationId();
  }

  const existing = localStorage.getItem(FCM_INSTALLATION_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const nextValue = createInstallationId();
  localStorage.setItem(FCM_INSTALLATION_ID_STORAGE_KEY, nextValue);
  return nextValue;
};

const persistMessagingToken = (token) => {
  if (typeof localStorage === "undefined" || !token) {
    return;
  }

  localStorage.setItem(FCM_BROWSER_TOKEN_STORAGE_KEY, token);
};

const readPendingCleanupMarker = () => {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const rawValue = localStorage.getItem(FCM_PENDING_CLEANUP_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    console.warn("[FCM] Failed to parse pending cleanup marker:", error);
    localStorage.removeItem(FCM_PENDING_CLEANUP_STORAGE_KEY);
    return null;
  }
};

const persistPendingCleanupMarker = (marker) => {
  if (typeof localStorage === "undefined") {
    return;
  }

  localStorage.setItem(
    FCM_PENDING_CLEANUP_STORAGE_KEY,
    JSON.stringify(marker)
  );
};

const clearPendingCleanupMarker = () => {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(FCM_PENDING_CLEANUP_STORAGE_KEY);
  }
};

const attachForegroundListener = (messaging) => {
  if (foregroundListenerAttached) {
    return;
  }

  foregroundListenerAttached = true;
  onMessage(messaging, (payload) => {
    console.log("[FCM] Foreground message:", payload);
    const title =
      payload?.notification?.title || payload?.data?.title || "Notification";
    const body = payload?.notification?.body || payload?.data?.body || "";
    alert(`${title}: ${body}`);
  });
};

const postTokenMutation = async (
  path,
  accessToken,
  token,
  extraBody = {}
) => {
  return fetch(resolveApiUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ token, ...extraBody }),
    credentials: "include",
  });
};

export function readCurrentInstallationId() {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(FCM_INSTALLATION_ID_STORAGE_KEY) || null;
}

export function clearLocalFirebaseNotificationState() {
  lastRegistrationSignature = null;

  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(FCM_BROWSER_TOKEN_STORAGE_KEY);
  }
}

export async function unregisterFirebaseNotificationToken(options = {}) {
  const accessToken = readStoredAuthToken(options.authTokenOverride);
  const token = options.messagingToken || readStoredMessagingToken();
  const installationId =
    options.installationId || getOrCreateInstallationId();

  if (!accessToken) {
    console.info(
      "[FCM] Skipping token cleanup because there is no authenticated session."
    );
    return false;
  }

  if (!token) {
    console.info(
      "[FCM] Skipping token cleanup because no cached messaging token was found."
    );
    return false;
  }

  try {
    const resp = await postTokenMutation(
      "/notifications/unregister-token",
      accessToken,
      token
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      console.warn("[FCM] unregister-token failed:", resp.status, text);
      persistPendingCleanupMarker({
        token,
        installationId,
        timestamp: new Date().toISOString(),
      });
      return false;
    }

    console.info("[FCM] Token cleanup completed on backend.");
    clearPendingCleanupMarker();
    return true;
  } catch (err) {
    console.warn("[FCM] Token cleanup request failed:", err);
    persistPendingCleanupMarker({
      token,
      installationId,
      timestamp: new Date().toISOString(),
    });
    return false;
  }
}

export async function requestFirebaseNotificationPermission() {
  const accessToken = readStoredAuthToken();
  const installationId = getOrCreateInstallationId();
  const pendingCleanupMarker = readPendingCleanupMarker();

  if (!accessToken) {
    console.info(
      "[FCM] Skipping notification registration because there is no authenticated admin session."
    );
    return null;
  }

  if (!("serviceWorker" in navigator)) {
    console.warn("[FCM] Service workers are not supported in this browser.");
    return null;
  }

  if (registrationPromise) {
    return registrationPromise;
  }

  registrationPromise = (async () => {
    try {
      const registration = await navigator.serviceWorker.register(
        FCM_SERVICE_WORKER_PATH
      );
      const messaging = getMessaging(app);
      attachForegroundListener(messaging);

      // NOTE: Let getToken trigger the permission prompt like the existing flow.
      const token = await getToken(messaging, {
        vapidKey: FCM_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        console.info(
          "[FCM] No registration token yet (permission not granted or blocked)."
        );
        return null;
      }

      persistMessagingToken(token);

      const registrationSignature = `${accessToken}:${token}`;
      if (lastRegistrationSignature === registrationSignature) {
        console.debug(
          "[FCM] Skipping duplicate token registration for the current session."
        );
        return token;
      }

      const resp = await postTokenMutation(
        "/notifications/register-token",
        accessToken,
        token,
        { installationId }
      );

      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        console.warn("[FCM] register-token failed:", resp.status, text);
        if (
          resp.status === 409 &&
          pendingCleanupMarker?.token === token &&
          pendingCleanupMarker?.installationId === installationId
        ) {
          console.warn(
            "[FCM] Registration hit a stale same-browser token conflict before recovery could complete."
          );
        }
        return null;
      }

      lastRegistrationSignature = registrationSignature;
      if (
        pendingCleanupMarker?.token === token &&
        pendingCleanupMarker?.installationId === installationId
      ) {
        clearPendingCleanupMarker();
      }
      console.info("[FCM] Token registered on backend.");
      return token;
    } catch (err) {
      console.error("[FCM] Firebase messaging registration error", err);
      return null;
    } finally {
      registrationPromise = null;
    }
  })();

  return registrationPromise;
}
